/**
 * .tuMetrics {"tu1": 0.92, "tu2": 0.94, "tu3": 2, "notes": "Pure time-tracking field updates for card moves", "tuVersion": "2.5"}
 * .help "How the clock is committed when a task hits Done, and how History labels seconds."
 */
import type { Card } from '../../types/index.ts';
import { isWorkCardType } from '../../utils/cardTypeUtils.ts';

export const MAX_SESSION_ACCRUE_SECONDS = 12 * 60 * 60;

export function formatTimeForHistory(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const m = Math.floor(totalSeconds / 60);
  const h = Math.floor(m / 60);
  const mins = m % 60;
  if (h > 0) return `${h}h ${mins}m`;
  return `${m}m`;
}

type ClockCard = Pick<
  Card,
  'startedAt' | 'pausedAt' | 'lastWorkSessionStart' | 'totalWorkSeconds' | 'type'
>;

/** Accrue the open session onto totalWorkSeconds, capped at 12 hours. */
export function accrueOpenSessionSeconds(card: ClockCard, nowMs: number = Date.now()): number {
  const total = card.totalWorkSeconds || 0;
  if (!card.lastWorkSessionStart) return total;
  const sessionStart = new Date(card.lastWorkSessionStart).getTime();
  const sessionSeconds = Math.floor((nowMs - sessionStart) / 1000);
  return total + Math.min(Math.max(0, sessionSeconds), MAX_SESSION_ACCRUE_SECONDS);
}

export function timeFieldsOnReopenFromDone(): Pick<Card, 'endedAt'> {
  return { endedAt: null };
}

/**
 * When a task moves to done: stop the clock, persist accrued seconds, set endedAt.
 * Keep startedAt so Reports/timesheets still know the work window (clearing it used
 * to dump the lifetime total onto a single ended day with no span to sanity-check).
 */
export function timeFieldsOnMoveToDone(
  card: ClockCard,
  nowIso: string,
  nowMs: number = Date.now(),
): { updates: Partial<Card>; committedTotalSeconds: number | undefined } {
  if (!isWorkCardType(card.type)) {
    return { updates: {}, committedTotalSeconds: undefined };
  }
  if (card.startedAt || card.pausedAt || card.lastWorkSessionStart) {
    const newTotalSeconds = accrueOpenSessionSeconds(card, nowMs);
    return {
      updates: {
        lastWorkSessionStart: null,
        pausedAt: null,
        totalWorkSeconds: newTotalSeconds,
        endedAt: nowIso,
      },
      committedTotalSeconds: newTotalSeconds,
    };
  }
  return { updates: { endedAt: nowIso }, committedTotalSeconds: undefined };
}
